import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authService } from '../api/auth';
import { LoginRequest, AuthState } from '../types';

export const useAuth = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Initialize auth state on mount
  useEffect(() => {
    const user = authService.getCurrentUser();
    const isAuthenticated = authService.isAuthenticated();
    setAuthState({
      user,
      isAuthenticated,
      isLoading: false,
    });
  }, []);

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: (data) => {
      console.log('Login successful, data:', data);
      setAuthState({
        user: data.user,
        isAuthenticated: true,
        isLoading: false,
      });
      queryClient.clear(); // Clear all cached data on login
    },
    onError: () => {
      setAuthState(prev => ({ ...prev, isLoading: false }));
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authService.logout(),
    onSettled: () => {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      queryClient.clear(); // Clear all cached data on logout
      navigate('/login'); // Redirect to login page
    },
  });

  const login = useCallback(
    (credentials: LoginRequest) => {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      return loginMutation.mutateAsync(credentials);
    },
    [loginMutation]
  );

  const logout = useCallback(() => {
    return logoutMutation.mutateAsync();
  }, [logoutMutation]);

  const hasRole = useCallback((roles: string[]) => {
    return authService.hasRole(roles);
  }, []);

  const canAccess = useCallback((roles?: string[]) => {
    return authService.canAccess(roles);
  }, []);

  return {
    ...authState,
    login,
    logout,
    hasRole,
    canAccess,
    isLoginLoading: loginMutation.isPending,
    isLogoutLoading: logoutMutation.isPending,
    loginError: loginMutation.error,
  };
};
